import { useRef, useState, useEffect } from "react";
import { getMetaData } from "../../utils/Constant";
import FAQ from "../FAQ/FAQ";
import {  Row, Col } from "react-bootstrap";

// Bootstrap-based single-page placeholder generator with improved UI
export default function PlaceholderImageGenerator() {
  const canvasRef = useRef(null);
  const prevUrlRef = useRef(null);

  const faqData = [
    {
      id: 1,
      question: "Is this free to use?",
      answer:
        "Yes, completely free with no sign-up required. Unlimited generations are allowed.",
    },
    {
      id: 2,
      question: "Can I use these images commercially?",
      answer:
        "Absolutely. All generated images are royalty-free and safe for commercial use.",
    },
    {
      id: 3,
      question: "What is the maximum image size?",
      answer:
        "Most resolutions from 1x1 up to 5000x5000 pixels. Custom inputs are accepted, but very large sizes may take longer to generate.",
    },
    {
      id: 4,
      question: "What file formats can I download?",
      answer:
        "PNG, JPEG, and Webp are standard. PNG preserves transparency, JPEG is smaller for photos, and WebP load faster.",
    },
    {
      id: 5,
      question: "Does it support gradients or advanced effects?",
      answer:
        "Basic solid colors and simple gradients are supported. For complex effects, consider exporting to tools like Photoshop.",
    },
    {
      id: 6,
      question: "Is there a limit on usage?",
      answer:
        "No daily limits, but heavy use might trigger temporary rate limiting to prevent abuse.",
    },
    {
      id: 7,
      question: "How do I contact support?",
      answer:
        "Visit the main SampleFiles.dev site for a contact form or email samplefiles.dev@gmail.com for issues.",
    },
  ];
  // Dimensions
  const [width, setWidth] = useState(640);
  const [height, setHeight] = useState(360);
  const MAX_DIM = 5000;

  // Background
  const [bgType, setBgType] = useState("solid");
  const [bgColor1, setBgColor1] = useState("#6c757d");
  const [bgColor2, setBgColor2] = useState("#495057");
  const [gradientDirection, setGradientDirection] = useState("tb");

  // Text
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#ffffff");
  const [textSize, setTextSize] = useState(24);

  // Border & effects
  const [borderColor, setBorderColor] = useState("#000000");
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderRadius, setBorderRadius] = useState(0);

  // File & format
  const [format, setFormat] = useState("png");
  const [quality, setQuality] = useState(0.9);
  const [targetSizeMB, setTargetSizeMB] = useState("");

  // Output
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatedBytes, setGeneratedBytes] = useState(null);
  const [error, setError] = useState(null);

  // Presets
  const presets = [
    { label: "1:1", w: 300, h: 300 },
    { label: "16:9", w: 640, h: 360 },
    { label: "4:3", w: 800, h: 600 },
    { label: "Banner", w: 1200, h: 300 },
  ];

  // Helper: revoke previous URL when replaced
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    };
  }, []);

  // Fetch metadata
  useEffect(() => {
    const metaData = getMetaData("placeholder-image-generator");
    document.title = metaData.title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  // Validation
  const validate = (w, h) => {
    const W = Math.round(Number(w) || 0);
    const H = Math.round(Number(h) || 0);
    if (W <= 0 || H <= 0) return "Width and height must be positive numbers.";
    if (W > MAX_DIM || H > MAX_DIM)
      return `Max dimension is ${MAX_DIM} to avoid memory issues.`;
    return null;
  };

  // Draw background
  const drawBackground = (ctx, w, h) => {
    if (bgType === "solid") {
      ctx.fillStyle = bgColor1;
      ctx.fillRect(0, 0, w, h);
      return;
    }

    if (bgType === "gradient") {
      let grad;
      if (gradientDirection === "tb") {
        grad = ctx.createLinearGradient(0, 0, 0, h);
      } else if (gradientDirection === "lr") {
        grad = ctx.createLinearGradient(0, 0, w, 0);
      } else {
        grad = ctx.createLinearGradient(0, 0, w, h);
      }
      grad.addColorStop(0, bgColor1);
      grad.addColorStop(1, bgColor2);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }
  };

  // Draw border with rounded corners
  const drawBorder = (ctx, w, h) => {
    if (borderWidth <= 0) return;

    const r = Math.min(borderRadius, Math.min(w, h) / 2);
    ctx.save();
    ctx.lineWidth = borderWidth * 2;
    ctx.strokeStyle = borderColor;

    ctx.beginPath();
    ctx.moveTo(r, borderWidth);
    ctx.lineTo(w - r, borderWidth);
    ctx.quadraticCurveTo(
      w - borderWidth,
      borderWidth,
      w - borderWidth,
      r + borderWidth
    );
    ctx.lineTo(w - borderWidth, h - r - borderWidth);
    ctx.quadraticCurveTo(
      w - borderWidth,
      h - borderWidth,
      w - r - borderWidth,
      h - borderWidth
    );
    ctx.lineTo(r + borderWidth, h - borderWidth);
    ctx.quadraticCurveTo(
      borderWidth,
      h - borderWidth,
      borderWidth,
      h - r - borderWidth
    );
    ctx.lineTo(borderWidth, r + borderWidth);
    ctx.quadraticCurveTo(
      borderWidth,
      borderWidth,
      r + borderWidth,
      borderWidth
    );
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  };

  // Draw text
  const drawText = (ctx, w, h) => {
    const displayText = text.trim() ? text : `${w} × ${h}`;
    const textColorToUse = text.trim() ? textColor : "rgba(255,255,255,0.5)";
    const fontSizeToUse = text.trim() ? textSize : 20;

    ctx.fillStyle = textColorToUse;
    ctx.font = `${fontSizeToUse}px Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(displayText, w / 2, h / 2);
  };

  // Core render function with target size support (from first code)
  const renderCanvasToBlob = async (
    options = { mime: "image/png", quality: 0.9, targetBytes: null }
  ) => {
    setError(null);
    const w = Math.round(Number(width) || 0);
    const h = Math.round(Number(height) || 0);
    const v = validate(w, h);
    if (v) {
      setError(v);
      throw new Error(v);
    }

    const canvas = canvasRef.current;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // Clear canvas
    ctx.clearRect(0, 0, w, h);

    // Apply rounded corners clipping
    const r = Math.min(borderRadius, Math.min(w, h) / 2);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.quadraticCurveTo(w, 0, w, r);
    ctx.lineTo(w, h - r);
    ctx.quadraticCurveTo(w, h, w - r, h);
    ctx.lineTo(r, h);
    ctx.quadraticCurveTo(0, h, 0, h - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.clip();

    // Draw background
    drawBackground(ctx, w, h);
    ctx.restore();

    // Draw border
    drawBorder(ctx, w, h);

    // Draw text
    drawText(ctx, w, h);

    const mime =
      options.mime ||
      (format === "jpg"
        ? "image/jpeg"
        : format === "webp"
        ? "image/webp"
        : "image/png");

    const q = mime === "image/png" ? undefined : options.quality ?? quality;

    // Return a blob
    const blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b), mime, q)
    );
    if (!blob) throw new Error("Canvas toBlob failed");

    // Size targeting logic (from first code)
    let finalBlob = blob;
    if (options.targetBytes) {
      // If blob smaller than target, append padding bytes
      if (finalBlob.size < options.targetBytes) {
        const padding = new Uint8Array(options.targetBytes - finalBlob.size);
        finalBlob = new Blob([finalBlob, padding], { type: mime });
      } else if (finalBlob.size > options.targetBytes) {
        // For lossy formats, try binary search on quality to get smaller
        if (mime !== "image/png") {
          let low = 0.05;
          let high = q ?? 0.95;
          let best = finalBlob;
          for (let i = 0; i < 6; i++) {
            const mid = (low + high) / 2;
            // Re-encode
            const b2 = await new Promise((res) =>
              canvas.toBlob((b) => res(b), mime, mid)
            );
            if (!b2) break;
            if (b2.size <= options.targetBytes) {
              best = b2;
              high = mid;
            } else {
              low = mid;
            }
          }
          finalBlob = best;
        }
      }
    }

    return finalBlob;
  };

  // Live preview
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const targetBytes = targetSizeMB
          ? Math.round(parseFloat(targetSizeMB) * 1024 * 1024)
          : null;
        const mime =
          format === "jpg"
            ? "image/jpeg"
            : format === "webp"
            ? "image/webp"
            : "image/png";

        const blob = await renderCanvasToBlob({
          mime,
          quality,
          targetBytes,
        });

        if (cancelled) return;
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setPreviewUrl(url);
        setGeneratedBytes(blob.size);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();

    return () => (cancelled = true);
  }, [
    width,
    height,
    bgType,
    bgColor1,
    bgColor2,
    gradientDirection,
    text,
    textColor,
    textSize,
    borderColor,
    borderWidth,
    borderRadius,
    format,
    quality,
    targetSizeMB,
  ]);

  // Actions
  const handleDownload = async () => {
    try {
      const mime =
        format === "jpg"
          ? "image/jpeg"
          : format === "webp"
          ? "image/webp"
          : "image/png";
      const targetBytes = targetSizeMB
        ? Math.round(parseFloat(targetSizeMB) * 1024 * 1024)
        : null;

      const blob = await renderCanvasToBlob({ mime, quality, targetBytes });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `placeholder_${width}x${height}.${
        format === "jpg" ? "jpg" : format
      }`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          {/* Header Section */}
          <Row className="text-center mb-4">
            <Col>
              <h1 className="display-4 fw-bold text-black mb-3">
                Free Placeholder Image Generator
              </h1>
              <p className="lead text-muted">
                Free & fast tools to generate <strong>JSON</strong>. No signup,
                no hassle – just quick, secure conversions.
              </p>
            </Col>
          </Row>
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Advanced Placeholder Image Generator</h4>
            </div>

            <div className="card-body">
              <div className="row">
                {/* Controls */}
                <div className="col-md-5 mb-4">
                  {/* Dimensions */}
                  <h6>Dimensions</h6>
                  <div className="row g-2 mb-3">
                    <div className="col">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Width"
                        value={width}
                        onChange={(e) =>
                          setWidth(Math.max(1, parseInt(e.target.value) || 1))
                        }
                        min="1"
                        max="5000"
                      />
                    </div>
                    <div className="col-auto d-flex align-items-center">×</div>
                    <div className="col">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Height"
                        value={height}
                        onChange={(e) =>
                          setHeight(Math.max(1, parseInt(e.target.value) || 1))
                        }
                        min="1"
                        max="5000"
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {presets.map((preset) => (
                      <button
                        key={preset.label}
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setWidth(preset.w);
                          setHeight(preset.h);
                        }}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>

                  <hr />

                  {/* Background */}
                  <div className="mb-3">
                    <label className="form-label">Background Type</label>
                    <select
                      className="form-select"
                      value={bgType}
                      onChange={(e) => setBgType(e.target.value)}
                    >
                      <option value="solid">Solid Color</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </div>

                  {bgType === "solid" ? (
                    <div className="mb-3">
                      <label className="form-label">Background Color</label>
                      <input
                        type="color"
                        className="form-control form-control-color w-100"
                        value={bgColor1}
                        onChange={(e) => setBgColor1(e.target.value)}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="row g-2 mb-2">
                        <div className="col">
                          <label className="form-label">Color 1</label>
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={bgColor1}
                            onChange={(e) => setBgColor1(e.target.value)}
                          />
                        </div>
                        <div className="col">
                          <label className="form-label">Color 2</label>
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={bgColor2}
                            onChange={(e) => setBgColor2(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Gradient Direction</label>
                        <select
                          className="form-select"
                          value={gradientDirection}
                          onChange={(e) => setGradientDirection(e.target.value)}
                        >
                          <option value="tb">Top to Bottom</option>
                          <option value="lr">Left to Right</option>
                          <option value="tlbr">Diagonal</option>
                        </select>
                      </div>
                    </>
                  )}

                  <hr />

                  {/* Border */}
                  <div className="mb-3">
                    <label className="form-label">
                      Border Width: {borderWidth}px
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="50"
                      value={borderWidth}
                      onChange={(e) => setBorderWidth(parseInt(e.target.value))}
                    />
                  </div>

                  {borderWidth > 0 && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Border Color</label>
                        <input
                          type="color"
                          className="form-control form-control-color w-100"
                          value={borderColor}
                          onChange={(e) => setBorderColor(e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          Border Radius: {borderRadius}px
                        </label>
                        <input
                          type="range"
                          className="form-range"
                          min="0"
                          max={Math.min(width, height) / 2}
                          value={borderRadius}
                          onChange={(e) =>
                            setBorderRadius(parseInt(e.target.value))
                          }
                        />
                      </div>
                    </>
                  )}

                  <hr />

                  {/* Text */}
                  <div className="mb-3">
                    <label className="form-label">Text Overlay</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 400×300"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col">
                      <label className="form-label">Text Color</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Font Size</label>
                      <input
                        type="range"
                        className="form-range"
                        min="12"
                        max="100"
                        value={textSize}
                        onChange={(e) => setTextSize(parseInt(e.target.value))}
                      />
                      <small className="text-muted d-block text-center">
                        {textSize}px
                      </small>
                    </div>
                  </div>

                  {/* Format & Quality */}
                  <div className="row g-2 mb-3">
                    <div className="col">
                      <label className="form-label">Format</label>
                      <select
                        className="form-select"
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                      >
                        <option value="png">PNG</option>
                        <option value="jpg">JPEG</option>
                        <option value="webp">WebP</option>
                      </select>
                    </div>
                    {(format === "jpg" || format === "webp") && (
                      <div className="col">
                        <label className="form-label">
                          Quality: {(quality * 100).toFixed(0)}%
                        </label>
                        <input
                          type="range"
                          className="form-range"
                          min="0.1"
                          max="1"
                          step="0.05"
                          value={quality}
                          onChange={(e) =>
                            setQuality(parseFloat(e.target.value))
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Target Size (from first code) */}
                  <div className="mb-3">
                    <label className="form-label">
                      Target file size (MB) — optional
                    </label>
                    <input
                      className="form-control"
                      type="number"
                      step="0.1"
                      min={0}
                      value={targetSizeMB}
                      onChange={(e) => setTargetSizeMB(e.target.value)}
                      placeholder="e.g. 1"
                    />
                    <div className="form-text">
                      If set, system will try to inflate or compress to match
                      target size (best effort).
                    </div>
                  </div>

                  <button
                    className="btn btn-success w-100"
                    onClick={handleDownload}
                  >
                    Download Image
                  </button>

                  {error && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {error}
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="col-md-7">
                  <h6>Preview</h6>
                  <div
                    className="border rounded p-3 bg-light d-flex justify-content-center align-items-center"
                    style={{ minHeight: "320px" }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={`Placeholder ${width}×${height}`}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          borderRadius: "8px",
                        }}
                      />
                    ) : (
                      <div className="text-muted">Preview will appear here</div>
                    )}
                  </div>

                  <div className="text-center mt-2">
                    <small className="text-muted">
                      {width} × {height} • {format.toUpperCase()}
                      {generatedBytes && ` • ${formatSize(generatedBytes)}`}
                      {targetSizeMB && ` • Target: ${targetSizeMB} MB`}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Features Section */}

      <div className="App bg-light">
        {/* Header Section */}
        <section className="bg-white py-5 shadow-sm">
          <div className="container m-3">
            {/* <div className="text-center mb-4">
              <i
                className="bi bi-image-fill text-primary"
                style={{ fontSize: "3rem" }}
              ></i>
            </div> */}
            <h2 className="display-6 text-center fw-bold mb-4">
              What is a Dummy Image Generator?
            </h2>
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="p-4 bg-light rounded-4 shadow-sm">
                  <p className="lead text-center mb-3">
                    A Dummy Image Generator is a tool that creates placeholder
                    images — temporary visuals used when the final images are
                    not yet available. Designers, and developers often use them
                    to test layouts, designs, or user interfaces without waiting
                    for real images.
                  </p>
                  <p className="lead text-center">
                    Customize image dimensions, background and text colors, and
                    add descriptive text to create professional placeholder
                    images that streamline your design workflow. Perfect for UI
                    mockups, responsive layout testing, and keeping your web
                    projects on track without delays.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How To Section */}
        <section className="py-5 bg-light">
          <div className="container">
            <h2 className="display-6 text-center fw-bold mb-4">
              How Do I Create a Dummy Image?
            </h2>
            <p className="text-center text-secondary mb-5">
              It’s really simple! Just follow these quick steps:
            </p>

            <div className="row g-4 justify-content-center">
              {/* Step 1 */}
              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 text-center p-3 hover-shadow transition">
                  <div className="mb-3 text-primary">
                    <i className="bi bi-sliders2 fs-1"></i>
                  </div>
                  <h5 className="fw-bold mb-2 text-dark">Step 1: Customize</h5>
                  <p className="text-muted small mb-0">
                    Change the dimension, format, background color, and border.
                    You can also set text color and add custom text to your
                    image.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 text-center p-3">
                  <div className="mb-3 text-primary">
                    <i className="bi bi-cpu fs-1"></i>
                  </div>
                  <h5 className="fw-bold mb-2 text-dark">Step 2: Generate</h5>
                  <p className="text-muted small mb-0">
                    Instantly generates placeholder image — preview its true
                    size and quality.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 text-center p-3">
                  <div className="mb-3 text-primary">
                    <i className="bi bi-cloud-arrow-down fs-1"></i>
                  </div>
                  <h5 className="fw-bold mb-2 text-dark">Step 3: Download</h5>
                  <p className="text-muted small mb-0">
                    “Download Image” to save it to your computer and use
                    anywhere.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 text-center p-3">
                  <div className="mb-3 text-primary">
                    <i className="bi bi-window fs-1"></i>
                  </div>
                  <h5 className="fw-bold mb-2 text-dark">Step 4: Use</h5>
                  <p className="text-muted small mb-0">
                    Upload your image to your website and use it as a
                    professional placeholder.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <FAQ
        title={"Frequently Asked Questions"}
        subTitle={
          "Everything you need to know about our placeholder image generator"
        }
        faqData={faqData}
      />
    </div>
  );
}
